// FormScreen (TEMPLATES.md): a section rail with issue counts, the active
// section's content, an optional preview panel (desktop) or sheet (phone
// and tablet), and a pinned save bar with dirty state. The frame is the
// viewport; the content region scrolls.
//
// Drawn through the kit (LOOKS.md Part 7): the layout is classes keyed off the
// frame's tier (`.fsp-frame[data-tier]`), never an inline grid.

import { useState, type ReactNode } from 'react';
import { Icon } from '../kit/Icon';
import { Button, Caption, Count, Dialog, Text } from '../kit/ui';
import { useTier } from '../shell/useTier';

export interface FormSection {
  id: string;
  title: string;
  errors: number;
  warnings: number;
  locked?: boolean;
  /** Fixed for a league that exists: shown, never editable. */
  fixed?: boolean;
}

export function FormScreen({
  header,
  sections,
  active,
  onSection,
  strip,
  content,
  preview,
  previewLabel = 'Preview',
  dirtyLabel,
  onDiscard,
  onSave,
  saveLabel = 'Save',
  saveDisabled,
}: {
  header: ReactNode;
  sections: FormSection[];
  active: string;
  onSection: (id: string) => void;
  /** "Start from a preset" for sections that have presets. */
  strip?: ReactNode;
  content: ReactNode;
  /** Rendered as a right panel on desktop, a sheet behind a button elsewhere. */
  preview?: ReactNode;
  previewLabel?: string;
  /** "3 changes"; null when nothing is dirty (the save bar hides). */
  dirtyLabel: string | null;
  onDiscard: () => void;
  onSave: () => void;
  saveLabel?: string;
  saveDisabled?: boolean;
}) {
  const tier = useTier();
  const phone = tier === 'phone';
  const wide = tier === 'desktop' || tier === 'large';
  const [previewOpen, setPreviewOpen] = useState(false);
  // A FORM OF ONE SECTION has nothing to pick between: no rail (the
  // commissioner's scoring tool, PLAN-2026-10-18 §2.5).
  const single = sections.length <= 1;

  const rail = (
    <div className={phone ? 'fsp-chip-row fsp-form-chips' : 'fsp-form-rail'}>
      {sections.map((s) => (
        <button
          key={s.id}
          type="button"
          className={`fsp-form-rail-item ${s.id === active ? 'active' : ''}`}
          aria-current={s.id === active ? 'true' : undefined}
          onClick={() => onSection(s.id)}
        >
          <span className="fsp-form-rail-name">
            {s.locked || s.fixed ? <Icon name="locked" size="sm" /> : null}
            {s.title}
          </span>
          {s.errors ? (
            <Count value={s.errors} tone="bad" />
          ) : s.warnings ? (
            <Count value={s.warnings} tone="warn" />
          ) : null}
        </button>
      ))}
    </div>
  );

  return (
    <div className="fsp-screen">
      <div className="fsp-pinned fsp-form-head">
        {header}
        {preview && !wide ? (
          <Button size="xs" icon="preview" onClick={() => setPreviewOpen(true)}>
            {previewLabel}
          </Button>
        ) : null}
      </div>
      {phone && !single ? <div className="fsp-pinned">{rail}</div> : null}
      <div
        className={`fsp-regions fsp-form-regions ${wide && preview ? 'fsp-form-with-preview' : ''} ${single ? 'fsp-form-single' : ''}`}
      >
        {phone || single ? null : (
          <section className="fsp-region fsp-region-plain" aria-label="Sections">
            {rail}
          </section>
        )}
        <section className="fsp-region" aria-label="Settings">
          {strip ? <div className="fsp-region-head">{strip}</div> : null}
          <div className="fsp-form-content">{content}</div>
        </section>
        {wide && preview ? (
          <section className="fsp-region" aria-label={previewLabel}>
            <div className="fsp-region-head">
              <Caption>{previewLabel}</Caption>
            </div>
            <div className="fsp-form-preview">{preview}</div>
          </section>
        ) : null}
      </div>
      {dirtyLabel ? (
        <div className="fsp-form-save">
          <Text weight="strong">{dirtyLabel}</Text>
          <span className="fsp-form-save-actions">
            <Button variant="quiet" onClick={onDiscard}>
              Discard
            </Button>
            <Button variant="primary" onClick={onSave} disabled={saveDisabled}>
              {saveLabel}
            </Button>
          </span>
        </div>
      ) : null}
      {preview && !wide ? (
        <Dialog opened={previewOpen} onClose={() => setPreviewOpen(false)} title={previewLabel}>
          {preview}
        </Dialog>
      ) : null}
    </div>
  );
}
